/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import queryString from "query-string";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue } from "recoil";
import { getLinkPreviews } from "../../api/Email";
import BrokenLinkSVG from "../../assets/icons/email/brokenlink.svg";
import BrokenLinkLargeSVG from "../../assets/icons/email/brokenlinkLarge.svg";
import EmailSelectorFamily, { EmailMode } from "../../atoms/EmailAtom";
import { EmailLinksAtom } from "../../atoms/EmailLinksAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { BUCKET_URL } from "../../config";
import { customTooltip } from "../../styles/general";
import useDebounce from "../../utils/UseDebounce";
import IconBerserkerMail from "../Icon";
import Loading from "../Loading";
import { TooltipText } from "../SideBar";

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div<{ length: number }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.length > 1 ? "150px 150px" : "213px"};
  grid-gap: 16px;
`;

const LinkContainer = styled.a`
  cursor: pointer;
  font-size: 15px;
  color: #2f2f2f;
  text-decoration: none;
  width: 100%;
`;

const LinkImage = styled.img<{ length: number }>`
  width: 100%;
  height: ${(props) => (props.length > 1 ? "84px" : "119px")};
  border-radius: 5px;
`;

const BrokenLinkImg = styled.img`
  border-radius: 5px;
  width: 194px;

  object-fit: contain;
  /**
  * Fix the image that doesn't look like it's in the middle because of the top
  * left stop sign
  */
  margin-right: 12px;
`;

const BrokenLinkButton = styled.button`
  color: #c41700;
  border: 1px solid #c41700;
  border-radius: 5px;
  background-color: white;
  padding: 5px 10px;
  font-weight: 600;
  width: 182px;
  /* cursor: pointer; */
  /* :hover {
    color: #ff1e00;
    border-color: #ff1e00;
  } */
`;

const BrokenLinksDiv = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  height: 200px;
`;

const LinkText = styled.div`
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-size: 15px;
  text-align: center;
`;

const NoLinkPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 200px;
  gap: 19px;

  font-size: 14px;
  color: #707070;
  font-weight: 600;

  border: 1px solid #dddddd;
  border-radius: 4px;
`;

export const FindLinkRegex =
  /href=("|')https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/gi;

interface Props {
  areLinksReady: (loading: boolean) => void;
  mode: EmailMode;
}

export default function LinkPreviews(props: Props) {
  const { areLinksReady } = props;
  const user = useRecoilValue(UserAtom);
  const email = useRecoilValue(EmailSelectorFamily({ mode: props.mode }));

  // const interestingLinks = useRecoilValue(LinksAtom);
  const [activeLinks, setActiveLinks] = useRecoilState(EmailLinksAtom);
  const [oldLinks, setOldLinks] = useState<string[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedLinks = useDebounce(links, 2000);
  const refIndex = useRef(0);

  const showPreviewLink = user?.options?.showPreviewLink;

  // useEffect(() => {
  //   const interestingLinksURLs = interestingLinks.map((l) => l.url);
  //   const previousInterestingLinks = email.interestingURLs
  //     ? Object.keys(email.interestingURLs || {})
  //     : [];
  //   const newInterestingURLs = [
  //     ...previousInterestingLinks,
  //     ...links.filter((url) => interestingLinksURLs.includes(url)),
  //   ];
  //   const uniqueNewInterestingURLs = Array.from(new Set(newInterestingURLs));
  //   setActiveEmail((activeEmail) => ({
  //     ...activeEmail,
  //     interestingURLs: uniqueNewInterestingURLs,
  //   }));
  // }, [links, interestingLinks]);

  useEffect(() => {
    const hasUnlimitedLinks = !!user?.emailOptions?.unlimitedLinks;
    const maxLinksInEmail = hasUnlimitedLinks ? Infinity : 3;

    if (!showPreviewLink) {
      setActiveLinks({
        tooManyLinks: Array(debouncedLinks).length > maxLinksInEmail,
        links: (debouncedLinks as string[]).map((link) => ({
          link,
          valid: true,
          previewImageId: null,
        })),
      });
    } else {
      const index = refIndex.current;
      updateLinks(index);
    }
  }, [debouncedLinks, showPreviewLink, user]);

  useEffect(() => {
    const links = email.body.matchAll(FindLinkRegex);

    let next: IteratorResult<RegExpMatchArray, any>;
    let matchArray: string[] = [];

    while (!(next = links.next()).done) {
      const currentMatch = next.value;
      const url = currentMatch[0].replace('href="', "").replace("href='", "");
      const { index } = currentMatch;
      if (index) matchArray.push(url);
    }

    matchArray = Array.from(new Set(matchArray));

    // Compare new items with the old ones
    const isTheSame =
      matchArray.length === oldLinks.length &&
      matchArray.every((val, index) => val === oldLinks[index]);

    if (isTheSame) {
      return;
    }

    setOldLinks(matchArray);
    setLinks(matchArray);
  }, [email.body, oldLinks]);

  useEffect(() => {
    areLinksReady(loading);
  }, [loading]);

  async function updateLinks(n: number) {
    const hasUnlimitedLinks = !!user?.emailOptions?.unlimitedLinks;
    const maxLinksInEmail = hasUnlimitedLinks ? Infinity : 3;

    const workingLinks = debouncedLinks as string[];
    setLoading(true);

    if (workingLinks.length > maxLinksInEmail) {
      if (n === refIndex.current) {
        setActiveLinks({
          tooManyLinks: true,
          links: [],
        });
        setLoading(false);
      }
      return;
    }

    const newActiveLinks = await getLinkPreviews(workingLinks);

    if (n === refIndex.current) {
      setActiveLinks({
        tooManyLinks: false,
        links: newActiveLinks,
      });
      setLoading(false);
    }
  }

  function printUnprefixedURL(selectedValue: string) {
    if (selectedValue.toLowerCase().indexOf("https://") === 0) {
      return selectedValue.replace("https://", "").replace("Https://", "");
    } else if (selectedValue.toLowerCase().indexOf("http://") === 0) {
      return selectedValue.replace("http://", "").replace("Http://", "");
    }
    return selectedValue;
  }

  const memo = useMemo(() => {
    if (!showPreviewLink) return null;

    if (loading) {
      return (
        <Container>
          <Wrapper length={1}>
            <Loading />
          </Wrapper>
        </Container>
      );
    }

    const showLinks = activeLinks.tooManyLinks;

    if (activeLinks.links?.length === 0 && activeLinks?.tooManyLinks) {
      return (
        <BrokenLinksDiv>
          <BrokenLinkImg
            style={{ objectFit: "contain" }}
            src={BrokenLinkSVG.src}
          />
          <BrokenLinkButton>
            {activeLinks.tooManyLinks
              ? "Too Many Links"
              : "Broken Links Detected"}
          </BrokenLinkButton>
        </BrokenLinksDiv>
      );
    } else if (activeLinks.links?.length === 0)
      return (
        <NoLinkPreview>
          <img src="./images/no-link-preview.png" />
          <span>No link added</span>
        </NoLinkPreview>
      );

    return (
      <Container>
        <Wrapper length={showLinks ? 1 : activeLinks.links.length}>
          {showLinks ? (
            <BrokenLinksDiv>
              <BrokenLinkImg
                style={{ objectFit: "contain" }}
                src={BrokenLinkSVG.src}
              />
              <BrokenLinkButton>
                {activeLinks.tooManyLinks
                  ? "Too Many Links"
                  : "Broken Links Detected"}
              </BrokenLinkButton>
            </BrokenLinksDiv>
          ) : (
            activeLinks.links.map((link) => {
              if (link.valid)
                return (
                  <LinkContainer
                    key={queryString.exclude(link.link, ["__position__"])}
                    data-tip
                    data-for={queryString.exclude(link.link, ["__position__"])}
                    href={queryString.exclude(link.link, ["__position__"])}
                    target="_blank"
                  >
                    {link.valid && !link.previewImageId ? (
                      <IconBerserkerMail height={84} name="page" />
                    ) : (
                      <LinkImage
                        length={activeLinks.links.length}
                        style={{
                          objectFit: link.valid ? "cover" : "contain",
                          border: "1px solid #ccc",
                        }}
                        src={
                          link.previewImageId.includes("https://")
                            ? // New link, stored on Object Storage
                              link.previewImageId
                            : // Old link,
                              `${BUCKET_URL}/file/local/${link.previewImageId}`
                        }
                      />
                    )}
                    <LinkText>
                      {printUnprefixedURL(
                        queryString.exclude(link.link, ["__position__"])
                      )}
                    </LinkText>

                    <ReactTooltip
                      id={queryString.exclude(link.link, ["__position__"])}
                      place="right"
                      effect="solid"
                      backgroundColor="#333333"
                      css={customTooltip}
                    >
                      <TooltipText>
                        {printUnprefixedURL(
                          queryString.exclude(link.link, ["__position__"])
                        )}
                      </TooltipText>
                    </ReactTooltip>
                  </LinkContainer>
                );
              else {
                if (activeLinks.links.length === 1) {
                  return (
                    <BrokenLinksDiv>
                      <BrokenLinkImg
                        style={{ objectFit: "contain" }}
                        src={BrokenLinkSVG.src}
                      />
                      <BrokenLinkButton>
                        {activeLinks.tooManyLinks
                          ? "Too Many Links"
                          : "Broken Links Detected"}
                      </BrokenLinkButton>
                    </BrokenLinksDiv>
                  );
                } else {
                  return (
                    <BrokenLinksDiv
                      css={css`
                        height: auto;
                      `}
                    >
                      <LinkImage
                        length={activeLinks.links.length}
                        style={{
                          objectFit: link.valid ? "cover" : "contain",
                        }}
                        src={BrokenLinkLargeSVG.src}
                      />

                      <LinkText
                        css={css`
                          color: #c40000;
                        `}
                      >
                        {printUnprefixedURL(
                          queryString.exclude(link.link, ["__position__"])
                        )}
                      </LinkText>
                    </BrokenLinksDiv>
                  );
                }
              }
            })
          )}
        </Wrapper>
      </Container>
    );
  }, [loading, activeLinks, showPreviewLink]);

  return memo;
}
